// İSG uzmanı portalı — belge onay/red kontrolü: İzin personel Sheet'i, taşeron personel belgeleri
// sayfası ve araç Sheet'i AYNI kontrolü kullanır (GL #25). Slot sırası: İndir · Onayla · Reddet ·
// [Sil]; her slot 40px ve boşken de yerini korur. Reddet gerekçesi satır-içi bir editördür
// (Modal DEĞİL — Sheet içinde render edilir). Erişilebilirlik: düğme adları belge türünü taşır,
// editör açılınca odak textarea'ya gider, kapanınca düğmeye / satırın İndir düğmesine döner.
// Sonuç bildirimi (onaylandı/reddedildi) hook'tadır; `DocumentReviewFeedback` her kullanımda aynı
// canlı bölgeyi taşır — ekran okuyucu üç ekranda da sonucu duyar.
// Başarısız onay/red listeyi tazeler; satır yenilemede KALKARSA (ör. belge silinmiş, 404) hata satırla
// kaybolmaz: panel düzeyi Alert'e + canlı bölgeye taşınır, odak <body>'ye düşmez — çağıranın kalıcı
// denetimine iner (silme/çıkarma ile aynı durum+efekt deseni: uuid + sorgu zaman damgaları).

import {
  Alert,
  Button,
  DOCUMENT_REJECT_REASON_MAX,
  DocumentActionSlot,
  Field,
  TableCell,
  TableRow,
  Textarea,
  documentsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { Check, Download, X } from 'lucide-react';
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ReviewableDocument {
  /** Belge KAYDININ uuid'i (onay/red/silme hedefi) — §8 `documentUuid` DEĞİL. */
  uuid: string;
  /** §8 dosya kimliği — yalnız indirme. */
  documentUuid: string;
  approvalStatus: 'Pending' | 'Approved' | 'Rejected';
  /** Yerelleştirilmiş tür adı — düğmelerin erişilebilir adında kullanılır. */
  typeLabel: string;
  /** Same-type documents must not share button names: file name, else the validity text. */
  nameDetail?: string;
}

export const documentNameLabel = (typeLabel: string, detail?: string) =>
  detail ? `${typeLabel} (${detail})` : typeLabel;

/** Listeyi besleyen sorgunun zaman damgaları (TanStack `useQuery` sonucu bu şekli taşır). */
export interface ReviewSource {
  dataUpdatedAt: number;
  errorUpdatedAt: number;
}

export interface UseDocumentReviewOptions {
  approve: (documentUuid: string) => Promise<unknown>;
  reject: (documentUuid: string, reason: string) => Promise<unknown>;
  /**
   * Başarıdan VE hatadan sonra sahibi listeyi tazeler (önbellek geçersiz kılma). Hata da tazeler:
   * 404 (belge silinmiş) ya da bayat durum ekranda eski satırı bırakmasın.
   */
  invalidate: () => void;
  /** Listede ŞU AN görünen belgeler — başarısız işlemin satırı yenilemede kalktı mı? */
  documents: readonly { uuid: string }[];
  /** `documents`'ı besleyen sorgu; zaman damgalarının değişmesi yenilemenin bittiğini söyler. */
  source: ReviewSource;
  /** Satır kalkınca odağın ineceği kalıcı denetim ("Belge yükle" / "Belge ekle" / bölüm başlığı). */
  fallbackFocusRef: RefObject<HTMLElement | null>;
  /** Panelin kendi örtüsü (yükleme penceresi, silme diyaloğu) açık mı — açıksa odak çalınmaz. */
  overlayOpen: boolean;
}

/** `success` → görünür kısa başarı satırı; `lost` → satırı kalkmış hatanın metni (Alert'te görünür). */
type ReviewFeedbackState = { kind: 'success' | 'lost'; message: string } | null;

/** Bekleyen hata: başarısız belge + hata anındaki sorgu damgaları (satır kalkacak mı, bekliyoruz). */
type PendingFailure = ReviewSource & { uuid: string; error: unknown };

const downloadId = (uuid: string) => `doc-review-download-${uuid}`;
export const rejectRowId = (uuid: string) => `doc-review-reject-${uuid}`;

export function useDocumentReview({
  approve,
  reject,
  invalidate,
  documents,
  source,
  fallbackFocusRef,
  overlayOpen,
}: UseDocumentReviewOptions) {
  const { t } = useTranslation();
  const [rejectingUuid, setRejectingUuid] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  // Sonuç bildirimi — her işlemin BAŞINDA temizlenir: aynı metin tekrar gelince canlı bölge yeniden okur.
  const [feedback, setFeedback] = useState<ReviewFeedbackState>(null);
  const [failure, setFailure] = useState<PendingFailure | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Slot boşalınca (Onaylandı/Reddedildi) odak düşmesin: satırın İndir düğmesine in.
  const focusDownload = (uuid: string) =>
    requestAnimationFrame(() => document.getElementById(downloadId(uuid))?.focus());

  function closeEditor() {
    setRejectingUuid(null);
    setReason('');
    setTouched(false);
  }

  function startAction() {
    setFeedback(null);
    setFailure(null);
  }

  // Hata: listeyi tazele ve satırın kalkıp kalkmadığını izlemek için damgaları sakla.
  function recordFailure(uuid: string, error: unknown) {
    invalidate();
    setFailure({
      uuid,
      error,
      dataUpdatedAt: source.dataUpdatedAt,
      errorUpdatedAt: source.errorUpdatedAt,
    });
  }

  const approveMutation = useMutation({
    mutationFn: (documentUuid: string) => approve(documentUuid),
    onMutate: startAction,
    onSuccess: (_data, documentUuid) => {
      invalidate();
      setFeedback({ kind: 'success', message: t('documents.review.approveSuccess') });
      focusDownload(documentUuid);
    },
    onError: (error, documentUuid) => recordFailure(documentUuid, error),
  });

  const rejectMutation = useMutation({
    mutationFn: (vars: { documentUuid: string; reason: string }) =>
      reject(vars.documentUuid, vars.reason),
    onMutate: startAction,
    onSuccess: (_data, vars) => {
      closeEditor();
      invalidate();
      setFeedback({ kind: 'success', message: t('documents.review.rejectSuccess') });
      focusDownload(vars.documentUuid);
    },
    onError: (error, vars) => recordFailure(vars.documentUuid, error),
  });

  useEffect(() => {
    if (rejectingUuid) {
      reasonRef.current?.focus();
    }
  }, [rejectingUuid]);

  // Başarısız işlemin satırı yenilemede KALKTIYSA: hata metni panele + canlı bölgeye taşınır (satır-içi
  // red hatası satırla gitti), açık red editörü kapanır ve — panelde başka örtü yoksa — odak kalıcı
  // denetime iner (zamanlayıcı DEĞİL: satırın gerçekten kalktığı render). Yenileme bitti ve satır
  // duruyorsa talep düşer; hata satırda (red) ya da panelde (onay) zaten görünür.
  useEffect(() => {
    if (failure === null) {
      return;
    }
    if (!documents.some((doc) => doc.uuid === failure.uuid)) {
      setFeedback({ kind: 'lost', message: resolveApiErrorMessage(failure.error, t) });
      if (rejectingUuid === failure.uuid) {
        setRejectingUuid(null);
        setReason('');
        setTouched(false);
      }
      if (!overlayOpen) {
        fallbackFocusRef.current?.focus();
      }
      setFailure(null);
    } else if (
      source.dataUpdatedAt !== failure.dataUpdatedAt ||
      source.errorUpdatedAt !== failure.errorUpdatedAt
    ) {
      setFailure(null);
    }
  }, [
    failure,
    documents,
    rejectingUuid,
    overlayOpen,
    fallbackFocusRef,
    source.dataUpdatedAt,
    source.errorUpdatedAt,
    t,
  ]);

  const trimmed = reason.trim();
  const reasonIssue: 'required' | 'tooLong' | null =
    trimmed.length === 0
      ? 'required'
      : trimmed.length > DOCUMENT_REJECT_REASON_MAX
        ? 'tooLong'
        : null;

  return {
    rejectingUuid,
    reason,
    setReason,
    touched,
    touch: () => setTouched(true),
    reasonIssue,
    reasonRef,
    feedback,
    /** Başka bir belge işlemi (ör. silme) başlarken eski sonucu ve bekleyen hata izini kaldırır. */
    clearFeedback: startAction,
    approveMutation,
    rejectMutation,
    isBusy: (uuid: string) =>
      (approveMutation.isPending && approveMutation.variables === uuid) ||
      (rejectMutation.isPending && rejectMutation.variables?.documentUuid === uuid),
    openReject: (uuid: string, trigger: HTMLElement | null) => {
      triggerRef.current = trigger;
      setRejectingUuid(uuid);
      setReason('');
      setTouched(false);
      rejectMutation.reset();
    },
    closeReject: () => {
      if (rejectMutation.isPending) {
        return; // uçuştaki POST'u sıfırlama
      }
      closeEditor();
      rejectMutation.reset();
      requestAnimationFrame(() => triggerRef.current?.focus());
    },
    submitReject: (uuid: string) => {
      setTouched(true);
      if (reasonIssue !== null || rejectMutation.isPending) {
        return; // geçersiz ya da zaten gönderiliyor (çift gönderim yok)
      }
      rejectMutation.mutate({ documentUuid: uuid, reason: trimmed });
    },
  };
}

export type DocumentReview = ReturnType<typeof useDocumentReview>;

interface DocumentReviewActionsProps {
  review: DocumentReview;
  document: ReviewableDocument;
  /** `undefined` → 4. slot yok; `null` → boş ayrılmış slot; node → o içerik (Sil düğmesi). */
  deleteSlot?: ReactNode;
}

export function DocumentReviewActions({
  review,
  document,
  deleteSlot,
}: DocumentReviewActionsProps) {
  const { t } = useTranslation();
  const name = (key: string) =>
    t(key, { type: documentNameLabel(document.typeLabel, document.nameDetail) });
  const busy = review.isBusy(document.uuid);
  const rejecting = review.rejectingUuid === document.uuid;

  const download = useMutation({
    mutationFn: () => documentsApi.getDocument(document.documentUuid),
    onSuccess: (file) => {
      // Presigned GET — kısa ömürlü; URL/documentUuid log'lanmaz.
      window.open(file.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });

  return (
    <>
      <DocumentActionSlot>
        <Button
          id={downloadId(document.uuid)}
          variant="ghost"
          size="sm"
          onClick={() => download.mutate()}
          loading={download.isPending}
          aria-label={name('documents.review.download')}
          title={name('documents.review.download')}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DocumentActionSlot>
      <DocumentActionSlot>
        {document.approvalStatus !== 'Approved' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => review.approveMutation.mutate(document.uuid)}
            loading={
              review.approveMutation.isPending && review.approveMutation.variables === document.uuid
            }
            disabled={busy}
            aria-label={name('documents.review.approve')}
            title={name('documents.review.approve')}
          >
            <Check className="h-4 w-4 text-success" aria-hidden="true" />
          </Button>
        ) : null}
      </DocumentActionSlot>
      <DocumentActionSlot>
        {document.approvalStatus !== 'Rejected' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) =>
              rejecting ? review.closeReject() : review.openReject(document.uuid, e.currentTarget)
            }
            disabled={busy}
            aria-expanded={rejecting}
            aria-controls={rejecting ? rejectRowId(document.uuid) : undefined}
            aria-label={name('documents.review.reject')}
            title={name('documents.review.reject')}
          >
            <X className="h-4 w-4 text-danger" aria-hidden="true" />
          </Button>
        ) : null}
      </DocumentActionSlot>
      {deleteSlot !== undefined ? <DocumentActionSlot>{deleteSlot}</DocumentActionSlot> : null}
    </>
  );
}

/** Sheet.onEscapeKeyDown için: Esc reject editöründeyken Sheet kapanmaz (editör kendi Esc'ini işler). */
export function escapeStaysInReviewRow(event: KeyboardEvent) {
  if (event.target instanceof Element && event.target.closest('[data-review-editor]')) {
    event.preventDefault();
  }
}

export function DocumentRejectRow({
  review,
  documentUuid,
  colSpan,
}: {
  review: DocumentReview;
  documentUuid: string;
  colSpan: number;
}) {
  const { t } = useTranslation();
  if (review.rejectingUuid !== documentUuid) {
    return null;
  }
  const fieldId = `${rejectRowId(documentUuid)}-reason`;
  const showError = review.touched && review.reasonIssue !== null;
  return (
    <TableRow id={rejectRowId(documentUuid)}>
      <TableCell colSpan={colSpan}>
        <div
          data-review-editor
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              review.closeReject();
            }
          }}
          className="flex flex-col gap-2 py-2"
        >
          {review.rejectMutation.isError ? (
            <p role="alert" className="text-xs text-danger">
              {resolveApiErrorMessage(review.rejectMutation.error, t)}
            </p>
          ) : null}
          <Field
            htmlFor={fieldId}
            label={t('documents.review.reason.label')}
            required
            error={
              showError
                ? t(`documents.review.reason.${review.reasonIssue}`, {
                    max: DOCUMENT_REJECT_REASON_MAX,
                  })
                : undefined
            }
          >
            <Textarea
              id={fieldId}
              ref={review.reasonRef}
              rows={2}
              value={review.reason}
              onChange={(e) => review.setReason(e.target.value)}
              onBlur={review.touch}
              placeholder={t('documents.review.reason.placeholder')}
              invalid={showError}
            />
          </Field>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={review.closeReject}
              disabled={review.rejectMutation.isPending}
            >
              {t('contractors.common.cancel')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => review.submitReject(documentUuid)}
              loading={review.rejectMutation.isPending}
            >
              {t('documents.review.reject.confirm')}
            </Button>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Onay/red geri bildirimi, tablonun üstünde: onay hatası (ör. 403 `contractors:update` yok) sessiz
 * kalmaz (`role=alert`; satırı duran red hatası editör satırında). Satırı yenilemede kalkan hata
 * (onay ya da red) AYNI panel Alert'inde görünür ve canlı bölgede de okunur; başarı sonucu canlı
 * bölgede görünür kısa satırdır.
 */
export function DocumentReviewFeedback({ review }: { review: DocumentReview }) {
  const { t } = useTranslation();
  const { feedback } = review;
  const panelError =
    feedback?.kind === 'lost'
      ? feedback.message
      : review.approveMutation.isError
        ? resolveApiErrorMessage(review.approveMutation.error, t)
        : null;
  return (
    <>
      {panelError !== null ? <Alert tone="danger">{panelError}</Alert> : null}
      {/* Canlı bölge erişilebilirlik ağacında HEP kalır (boşken sr-only); <output> zaten role=status.
          Kalkan satırın hatası burada yalnız okunur (sr-only) — görünür metni yukarıdaki Alert'tir. */}
      <output
        aria-live="polite"
        className={feedback?.kind === 'success' ? 'block text-xs text-success' : 'sr-only'}
      >
        {feedback?.message}
      </output>
    </>
  );
}
